// TODO: Check if all options have the same currency

import Big from 'big.js'
import * as R from 'ramda'
import { BaseBasketItem, Basket, BasketItem, BasketItemType, isFlightBasketItem } from '../basket'
import {
  BalanceBooster,
  CarHireItemType,
  ExperienceItemType,
  FlightItemType,
  HotelItemType,
} from '../common'
import { SelectedPaymentOptions } from '../form-data'
import { getBalance, getSurchargesTotal } from './balance'
import { getCurrencyCodeFromBasket } from './findCurrencyCode'
import { Balance, PriceBreakdownTotals, TotalBalance } from './types'
import { getPaymentOptionAndConvert } from '../util'

const EMPTY_BALANCE: Balance = {
  aviosCollect: 0,
  aviosSpent: 0,
  cashTotal: new Big(0),
  cashBase: new Big(0),
  cashTaxes: new Big(0),
  cashTotalWithoutTaxes: new Big(0),
  isSalesTaxType: true,
  surcharges: { total: new Big(0), surchargesBreakdown: [] },
}
const EMPTY_TOTAL_BALANCE: TotalBalance = {
  aviosCollect: 0,
  aviosSpent: 0,
  cashTotal: new Big(0),
  cashBase: new Big(0),
  cashTaxes: new Big(0),
  cashTotalWithoutTaxes: new Big(0),
  surcharges: new Big(0),
  balanceBoosterPromoPaid: new Big(0),
}

const isEmptyBalance = (balance: Balance): boolean =>
  balance.aviosCollect === 0 && balance.aviosSpent === 0 && balance.cashTotal.eq(0)

const getBalanceBooster = (
  basketItem: BasketItem,
  paymentOption: SelectedPaymentOptions[BasketItemType]
) => {
  const {
    paymentOptions: { onlyCashPaymentOption },
  } = basketItem

  if (!paymentOption || onlyCashPaymentOption?.id !== paymentOption?.paymentOptionId) {
    return undefined
  }

  const balanceBoosterCode = paymentOption.balanceBooster

  return onlyCashPaymentOption.balanceBoosters?.find(({ code }) => code === balanceBoosterCode)
}

export const calculatePriceBreakdown = (
  basketItems: Basket['items'],
  selectedPaymentOptions: SelectedPaymentOptions
): PriceBreakdownTotals => {
  // Take only the first items of each type because that's what we support currently
  const getFirstItem = (items: BasketItem[]): BasketItem[] => (items[0] ? [items[0]] : [])

  const hotel = getFirstItem(basketItems.HOTEL)
  const carHire = getFirstItem(basketItems.CAR_HIRE)
  const experience = getFirstItem(basketItems.EXPERIENCE)
  const flight = getFirstItem(basketItems.FLIGHT)
  const currencyCode = getCurrencyCodeFromBasket(basketItems)

  const getSurchargesBreakdown = (currentBasketItem: BasketItem) =>
    currentBasketItem.surcharges.map((surcharge) => ({
      feeCode: surcharge.feeCode,
      feeName: surcharge.feeName,
      amount: new Big(surcharge.amount),
    }))

  const processBasketItem = (acc: Balance, currentBasketItem: BasketItem): Balance => {
    const paymentOption = selectedPaymentOptions[currentBasketItem.type]
    const selectedPaymentOptionId =
      paymentOption?.basketItemId === currentBasketItem.id && paymentOption.paymentOptionId

    if (!selectedPaymentOptionId) {
      return acc
    }

    const balanceBooster = getBalanceBooster(currentBasketItem, paymentOption)
    const balance = getBalance(currentBasketItem.paymentOptions, selectedPaymentOptionId)
    if (!balance) {
      return acc
    }

    const {
      aviosCollect,
      aviosSpent,
      cashTotal,
      cashBase,
      cashTaxes,
      isSalesTaxType,
      taxDetails,
      grandTotal,
    } = balance

    const surchargesTotal: Big = getSurchargesTotal(currentBasketItem)

    return {
      aviosCollect: acc.aviosCollect + aviosCollect + (balanceBooster?.earnedPoints ?? 0),
      aviosSpent: acc.aviosSpent + aviosSpent,
      cashTotal: (grandTotal ?? acc.cashTotal.plus(cashTotal)).plus(
        balanceBooster?.promoPaid.amount ?? 0
      ),
      cashTotalWithoutTaxes: acc.cashTotal
        .plus(cashTotal)
        .plus(balanceBooster?.promoPaid.amount ?? 0),
      cashBase: acc.cashBase.plus(cashBase),
      cashTaxes: acc.cashTaxes.plus(cashTaxes),
      isSalesTaxType,
      surcharges: {
        total: acc.surcharges.total.plus(surchargesTotal),
        surchargesBreakdown: getSurchargesBreakdown(currentBasketItem),
      },
      taxDetails,
      balanceBooster,
    }
  }

  const getCategoryResult = (items: BasketItem[]): Balance =>
    items.reduce(processBasketItem, EMPTY_BALANCE)

  const hotelResult = getCategoryResult(hotel)
  const carHireResult = getCategoryResult(carHire)
  const experienceResult = getCategoryResult(experience)
  const flightResult = getCategoryResult(flight)

  const totalSurcharges = hotelResult.surcharges.total.plus(experienceResult.surcharges.total)

  const total: TotalBalance = [hotelResult, carHireResult, experienceResult, flightResult].reduce(
    (acc, curr) => ({
      aviosCollect: acc.aviosCollect + curr.aviosCollect,
      aviosSpent: acc.aviosSpent + curr.aviosSpent,
      cashTotal: acc.cashTotal.plus(curr.cashTotal),
      cashTotalWithoutTaxes: acc.cashTotalWithoutTaxes.plus(curr.cashTotalWithoutTaxes),
      cashBase: acc.cashBase.plus(curr.cashBase),
      cashTaxes: acc.cashTaxes.plus(curr.cashTaxes),
      surcharges: totalSurcharges,
      balanceBoosterPromoPaid: acc.balanceBoosterPromoPaid?.plus(
        curr.balanceBooster?.promoPaid.amount ?? 0
      ),
    }),
    EMPTY_TOTAL_BALANCE
  )

  return {
    balance: {
      ...(!isEmptyBalance(hotelResult) && { [HotelItemType]: hotelResult }),
      ...(!isEmptyBalance(carHireResult) && { [CarHireItemType]: carHireResult }),
      ...(!isEmptyBalance(experienceResult) && { [ExperienceItemType]: experienceResult }),
      ...(!isEmptyBalance(flightResult) && { [FlightItemType]: flightResult }),
    },
    total,
    currencyCode,
  }
}

type ConvertedPaymentOption = {
  paymentOptionId: string
  cashValue?: {
    currency: string
    amount: number
  }
  aviosValue?: number
  balanceBoosters?: BalanceBooster[]
}

export const findPaymentOptionOrThrow = (
  selectedPaymentOptions: SelectedPaymentOptions,
  basketItem: BaseBasketItem
) => {
  const item = selectedPaymentOptions[basketItem.type]
  if (!item || item.basketItemId !== basketItem.id) {
    throw new Error('Could not find payment option')
  }

  const paymentOption = getPaymentOptionAndConvert<ConvertedPaymentOption>({
    paymentOptions: basketItem.paymentOptions,
    paymentOptionId: item.paymentOptionId,
    convertOnlyCashPaymentOption: (o) => ({
      paymentOptionId: o.id,
      cashValue: o.cashTotalValue,
      /**
       * Avios earned are debited to the account by paying with a negative amount of them :mildpanic:
       */
      aviosValue: -o.aviosCollectValue,
      balanceBoosters: o.balanceBoosters,
    }),
    convertPaymentOption: (o) => ({
      paymentOptionId: o.id,
      cashValue: o.cashValue,
      aviosValue: o.aviosValue ?? undefined,
    }),
  })

  if (!paymentOption) {
    throw new Error('Could not find payment option')
  }

  return paymentOption
}

export const getBundleSelectionIdForBasketItem = (
  basketItem: BasketItem,
  selectedPaymentOptions: SelectedPaymentOptions
): string =>
  isFlightBasketItem(basketItem)
    ? findPaymentOptionOrThrow(selectedPaymentOptions, basketItem).paymentOptionId
    : basketItem.bundleSelectionId

export const getBasketItemsArray = (items: Basket['items']) =>
  [items.HOTEL[0], items.CAR_HIRE[0], items.EXPERIENCE[0], items.FLIGHT[0]].filter(R.identity)

export const getCashSpentAndBundleSelectionIdsWithCashPayment = ({
  basket: { items },
  selectedPaymentOptions,
}: {
  basket: Basket
  selectedPaymentOptions: SelectedPaymentOptions
}) => {
  const { total } = calculatePriceBreakdown(items, selectedPaymentOptions)
  const cashSpent = total.cashTotalWithoutTaxes.toNumber()

  const bundleSelectionIdsWithCashPayment = getBasketItemsArray(items).flatMap((basketItem) => {
    const paymentOption = findPaymentOptionOrThrow(selectedPaymentOptions, basketItem)

    if (paymentOption.cashValue != null && paymentOption.cashValue.amount !== 0) {
      return [getBundleSelectionIdForBasketItem(basketItem, selectedPaymentOptions)]
    }

    return []
  })

  return { cashSpent, bundleSelectionIdsWithCashPayment }
}

export const isAviosPayment = (data: {
  basket: Basket
  selectedPaymentOptions: SelectedPaymentOptions
}) => {
  const { total } = calculatePriceBreakdown(data.basket.items, data.selectedPaymentOptions)
  return total.aviosSpent > 0
}

export const isFullAviosPayment = (data: {
  basket: Basket
  selectedPaymentOptions: SelectedPaymentOptions
}) => {
  const { bundleSelectionIdsWithCashPayment, cashSpent } =
    getCashSpentAndBundleSelectionIdsWithCashPayment(data)

  return cashSpent === 0 || bundleSelectionIdsWithCashPayment.length === 0
}
